import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import itPortalService from "../services/itPortalService";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {
  MdEdit,
  MdRefresh,
} from "react-icons/md";
import "../styles/ManageMember.css";
import "../styles/ManageUserTopCoin.css";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ManageTopCoinDistributionValue() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Notification state
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    Industry: "",
    Price: 5.0,
    TopCoin: 1,
    MiniTopCoin: 500,
    OperatingCountry: "UK",
  });

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await itPortalService.getTopCoinDistributionValues();
      const list = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setRecords(list);
    } catch (err) {
      console.error("Failed to load TopCoin distribution values:", err);
      if (err.response?.status === 403 && err.response?.data?.requires_it_portal_otp) {
        navigate("/it-portal");
      } else {
        showSnack(
          err.response?.data?.message || err.message || "Failed to load TopCoin distribution values.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to get clean industry name without ID prefix (e.g. "RetailBusiness" instead of "(1)RetailBusiness")
  const getIndustryDisplayName = (record) => {
    if (record?.industry?.TCIndustry) {
      return record.industry.TCIndustry;
    }
    if (record?.Industry) {
      return record.Industry.replace(/^\(\d+\)/, "").trim();
    }
    return "RetailBusiness";
  };

  // Open Edit Modal
  const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      Industry: getIndustryDisplayName(record),
      Price: record.Price !== undefined ? record.Price : 5.0,
      TopCoin: record.TopCoin !== undefined ? record.TopCoin : 1,
      MiniTopCoin: record.MiniTopCoin !== undefined ? record.MiniTopCoin : 500,
      OperatingCountry: record.OperatingCountry || "UK",
    });
    setIsEditModalOpen(true);
  };

  // Save Edit
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedRecord) return;

    setIsSaving(true);
    try {
      await itPortalService.updateTopCoinDistributionValue(selectedRecord.ID, {
        Price: parseFloat(formData.Price),
        TopCoin: parseInt(formData.TopCoin, 10),
        MiniTopCoin: parseInt(formData.MiniTopCoin, 10),
        OperatingCountry: formData.OperatingCountry.trim(),
      });

      showSnack(`"${formData.Industry}" TopCoin distribution value updated successfully!`);
      setIsEditModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(" ")
          : err.response?.data?.message || "Failed to update TopCoin distribution value.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Filtered records (by search query across all industries)
  const filteredRecords = records.filter((r) => {
    const searchLower = search.toLowerCase();
    const ind = getIndustryDisplayName(r).toLowerCase();
    const country = (r.OperatingCountry || "").toLowerCase();
    return ind.includes(searchLower) || country.includes(searchLower);
  });

  return (
    <div className="manage-member-layout">
      <SideBar />

      <div className="main-content">
        {/* Header Bar matching SuperAdmin style */}
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">TopCoin Distribution Value Management</h2>
            {/* <div className="it-header-actions">
              <button
                className="it-btn-refresh"
                onClick={fetchRecords}
                title="Refresh table data"
              >
                <MdRefresh size={18} /> Refresh
              </button>
            </div> */}
          </div>

          {/* Quick Statistics */}
          {/* <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Industries:</span>
              <span className="stat-value">{records.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Active:</span>
              <span className="stat-value" style={{ color: "#166534" }}>
                {records.filter((r) => !r.industry || Number(r.industry.Active) === 1).length}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Inactive:</span>
              <span className="stat-value" style={{ color: "#dc2626" }}>
                {records.filter((r) => r.industry && Number(r.industry.Active) === 0).length}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Default Price:</span>
              <span className="stat-value">£5.00</span>
            </div>
          </div> */}

          {/* Filter Bar */}
          <div className="filters">
            <div className="input-group" style={{ flexGrow: 1 }}>
              <label>Search Industry:</label>
              <input
                type="text"
                placeholder="Search by industry name"
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxLength={40}
              />
            </div>
          </div>
        </div>

        {/* Table Container matching image design */}
        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "70px" }}>ID</th>
                <th style={{ width: "220px" }}>Industry</th>
                {/* <th style={{ width: "120px" }}>Status</th> */}
                <th style={{ width: "140px" }}>Price</th>
                <th style={{ width: "120px" }}>TopCoin</th>
                <th style={{ width: "140px" }}>MiniTopCoin</th>
                <th style={{ width: "150px" }}>OperatingCountry</th>
                <th style={{ width: "110px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "30px" }}>
                    Loading TopCoin distribution values...
                  </td>
                </tr>
              ) : filteredRecords.length > 0 ? (
                filteredRecords.map((item) => (
                  <tr key={item.ID}>
                    <td style={{ fontWeight: 600 }}>{item.ID}</td>
                    <td>
                      <span className="it-badge it-badge-retail" style={{ fontWeight: 700 }}>
                        {getIndustryDisplayName(item)}
                      </span>
                    </td>
                    {/* <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: 600,
                          background:
                            item.industry && Number(item.industry.Active) === 0
                              ? "#fee2e2"
                              : "#dcfce7",
                          color:
                            item.industry && Number(item.industry.Active) === 0
                              ? "#dc2626"
                              : "#166534",
                          border: `1px solid ${
                            item.industry && Number(item.industry.Active) === 0
                              ? "#fecaca"
                              : "#bbf7d0"
                          }`,
                        }}
                      >
                        {item.industry && Number(item.industry.Active) === 0 ? "Inactive" : "Active"}
                      </span>
                    </td> */}
                    <td style={{ fontWeight: 700, color: "#166534" }}>
                      £{Number(item.Price ?? 5.0).toFixed(2)}
                    </td>
                    <td style={{ fontWeight: 600 }}>{item.TopCoin ?? 1}</td>
                    <td style={{ fontWeight: 600 }}>{item.MiniTopCoin ?? 500}</td>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: "12px",
                          background: "#f1f5f9",
                          fontWeight: 600,
                          fontSize: "13px",
                        }}
                      >
                        {item.OperatingCountry || "UK"}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="it-action-btn edit"
                        onClick={() => handleOpenEdit(item)}
                        title="Edit Distribution Value"
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#2563eb",
                        }}
                      >
                        <MdEdit size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "35px", color: "#64748b" }}>
                    No industries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedRecord && (
        <div className="it-modal-overlay">
          <div className="it-modal-box">
            <div className="it-modal-header">
              <h3>Edit TopCoin Distribution Value</h3>
              <button
                className="it-modal-close"
                onClick={() => !isSaving && setIsEditModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="it-modal-body">
                <div className="it-form-field">
                  <label>Industry</label>
                  <input
                    type="text"
                    value={formData.Industry}
                    disabled
                    style={{ background: "#f8fafc", cursor: "not-allowed" }}
                  />
                  <small>
                    Industry name is tied to TCIndustry.
                  </small>
                </div>

                <div className="it-form-field">
                  <label>Price (£) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.Price}
                    onChange={(e) =>
                      setFormData({ ...formData, Price: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="it-form-field">
                  <label>TopCoin *</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={formData.TopCoin}
                    onChange={(e) =>
                      setFormData({ ...formData, TopCoin: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="it-form-field">
                  <label>MiniTopCoin *</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.MiniTopCoin}
                    onChange={(e) =>
                      setFormData({ ...formData, MiniTopCoin: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="it-form-field">
                  <label>Operating Country *</label>
                  <input
                    type="text"
                    maxLength={50}
                    value={formData.OperatingCountry}
                    onChange={(e) =>
                      setFormData({ ...formData, OperatingCountry: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-cancel"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn"
                  style={{ marginTop: 0 }}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Snackbar Alert */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackClose} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
